import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';
import { getChangelogEntries, ChangelogEntry } from '../utils/changelogUtils';
import { Components } from 'react-markdown';

const Changelog: React.FC = () => {
  const { ref: h2Ref, inView: h2InView } = useInView({
    triggerOnce: true,
    threshold: 0.5,
  });
  const entries = getChangelogEntries();

  const handleNavigation = () => {
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const components: Components = {
    h1: ({ node, ...props }) => (
      <h1 className="text-2xl font-bold mb-4 text-white font-sans" {...props} />
    ),
    h2: ({ node, ...props }) => (
      <h2 className="text-xl font-bold mb-3 text-white font-sans" {...props} />
    ),
    h3: ({ node, ...props }) => (
      <h3 className="text-lg font-bold mb-2 text-white font-sans" {...props} />
    ),
    p: ({ node, ...props }) => (
      <p className="text-gray-200 mb-4 font-sans" {...props} />
    ),
    ul: ({ node, ...props }) => (
      <ul className="list-disc pl-6 mb-2 text-gray-200 font-sans [&>li>ul]:ml-1 [&>li>ol]:ml-1 [&>li>ul]:list-[circle] [&>li>ul>li>ul]:list-[square]" {...props} />
    ),
    ol: ({ node, ...props }) => (
      <ol className="list-decimal pl-6 mb-2 text-gray-200 font-sans [&>li>ul]:ml-1 [&>li>ol]:ml-1 [&>li>ol]:list-[lower-alpha] [&>li>ol>li>ol]:list-[lower-roman]" {...props} />
    ),
    li: ({ node, ...props }) => (
      <li className="mb-0.5 text-gray-200 font-sans" {...props} />
    ),
    code: ({ node, className, children, ...props }: any) => {
      const match = /language-(\w+)/.exec(className || '');
      if (!match) {
        return (
          <code className="bg-gray-700 rounded px-1 py-0.5 font-mono text-sm text-gray-200" {...props}>
            {children}
          </code>
        );
      }
      
      return (
        <SyntaxHighlighter
          style={vscDarkPlus}
          language={match[1]}
          PreTag="div"
          className="rounded-lg"
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      );
    },
  };

  return (
    <div className="min-h-screen bg-gray-900 py-8 sm:py-16">
      <div className="flex flex-col lg:flex-row">
        {/* Sidebar */}
        <div className="hidden lg:block w-72 fixed left-0 top-0 h-full pl-8 pr-8 pt-8">
          <div className="sticky top-8">
            <h3 className="text-lg font-semibold text-white mb-4 font-sans">Versions</h3>
            <nav className="space-y-3">
              {entries.map((entry) => (
                <a
                  key={entry.version}
                  href={`#version-${entry.version}`}
                  onClick={handleNavigation}
                  className="block py-2 px-3 rounded-lg transition-colors text-gray-400 hover:text-white hover:bg-gray-800 font-sans"
                >
                  <div className="font-medium">v{entry.version}</div>
                  <div className="text-sm text-gray-500 mt-1">
                    {new Date(entry.date).toLocaleDateString()}
                  </div>
                </a>
              ))}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 lg:pl-72">
          <div className="px-4 sm:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="flex justify-between items-center mb-8 sm:mb-16">
                <h1
                  ref={h2Ref}
                  className={`text-4xl sm:text-6xl font-bold text-center font-sans text-white ${
                    h2InView ? 'animate-title' : 'opacity-0 visibility-hidden'
                  }`}
                >
                  CHANGELOG
                </h1>
              </div>

              <div className="space-y-12 sm:space-y-24">
                {entries.map((entry, index) => (
                  <div
                    key={entry.version}
                    id={`version-${entry.version}`}
                    className="bg-gray-800 rounded-lg p-4 sm:p-8 transform transition-all duration-500 hover:scale-[1.02]"
                  >
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6">
                      <h2 className="text-2xl sm:text-3xl font-bold text-white font-sans mb-2 sm:mb-0">
                        Version {entry.version}
                      </h2>
                      <time className="text-gray-300 font-sans">
                        {new Date(entry.date).toLocaleDateString()}
                      </time>
                    </div>
                    <div className="prose prose-invert max-w-none">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={components}
                      >
                        {entry.content}
                      </ReactMarkdown>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 sm:mt-12 text-center">
                <Link
                  to="/"
                  className="inline-flex items-center text-red-500 hover:text-red-400 transition-colors font-sans"
                >
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 19l-7-7m0 0l7-7m-7 7h18"
                    />
                  </svg>
                  Back to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Changelog; 